import React, { useEffect, useRef, useState } from "react";
import {
  CheckCircle2,
  RefreshCcw,
  Send,
  Sparkles,
  Target,
  TrendingUp,
  Users,
  Activity,
  Building2,
  Award,
} from "lucide-react";
import { api } from "../lib/api";

import useCustomerData from "../hooks/useCustomerData";
import ConnectTallyBanner from "../components/ConnectTallyBanner";
import MetricDisplay from "../components/MetricDisplay";
import EditableValue from "../components/EditableValue";
import { OKREditor, DeptBudgetsEditor } from "../components/ParamEditors";
import { getCurrentCustomer } from "../lib/customerSession";

// ---------------------------------------------------------------------------
// Theme tokens
// ---------------------------------------------------------------------------
const STATUS = {
  green: { bar: "bg-emerald-500", text: "text-emerald-700", chip: "bg-emerald-100 text-emerald-700", left: "border-l-4 border-emerald-500", soft: "bg-emerald-50/70" },
  amber: { bar: "bg-amber-500",   text: "text-amber-700",   chip: "bg-amber-100 text-amber-700",     left: "border-l-4 border-amber-500",   soft: "bg-amber-50/70" },
  red:   { bar: "bg-red-500",     text: "text-red-700",     chip: "bg-red-100 text-red-700",         left: "border-l-4 border-red-500",     soft: "bg-red-50/70" },
  blue:  { bar: "bg-blue-500",    text: "text-blue-700",    chip: "bg-blue-100 text-blue-700",       left: "border-l-4 border-blue-500",    soft: "bg-blue-50/70" },
};

const DASH = "—";

function fmtINR(n) {
  if (n === null || n === undefined || Number.isNaN(Number(n))) return null;
  const num = Number(n);
  const v = Math.abs(num);
  const sign = num < 0 ? "-" : "";
  if (v >= 1_00_00_000) return `${sign}₹${(v / 1_00_00_000).toFixed(2)}Cr`;
  if (v >= 1_00_000)    return `${sign}₹${(v / 1_00_000).toFixed(1)}L`;
  if (v >= 1000)        return `${sign}₹${(v / 1000).toFixed(1)}K`;
  return `${sign}₹${Math.round(v).toLocaleString("en-IN")}`;
}

// ---------------------------------------------------------------------------
// AI Strategic Insights (real-only, no demo fallback)
// ---------------------------------------------------------------------------
function InsightCard({ insight }) {
  const s = STATUS[insight.severity] || STATUS.blue;
  return (
    <div data-testid={`ceo-insight-${insight.id}`} className={`rounded-lg ${s.left} ${s.soft} p-4`}>
      <h4 className="text-[13.5px] font-bold text-[#0F1B2D]">{insight.title}</h4>
      <p className="mt-1 text-[12.5px] leading-snug text-slate-700">{insight.body}</p>
      <p className="mt-2 text-[12px] font-semibold text-emerald-700">{insight.action}</p>
    </div>
  );
}

function InsightsGrid({ dataAvailable = false }) {
  const [items, setItems] = useState([]);
  const [source, setSource] = useState("loading");
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [refreshError, setRefreshError] = useState(false);
  const pollTimerRef = useRef(null);

  useEffect(() => () => { if (pollTimerRef.current) clearTimeout(pollTimerRef.current); }, []);

  const refresh = async (force = false, silent = false) => {
    if (pollTimerRef.current) { clearTimeout(pollTimerRef.current); pollTimerRef.current = null; }
    if (!silent) { setLoading(true); setRefreshError(false); }
    const res = await api.aiInsightsGenerate("ceo", api.ceoInsights, force);
    if (!silent) setLoading(false);
    if (res.ok) {
      const newItems = Array.isArray(res.data?.items) ? res.data.items : [];
      const bg = !!res.data?.generating;
      if (newItems.length >= 3) { setItems(newItems); setSource(res.data?.source === "ai" ? "ai" : "empty"); }
      else if (!bg) { setItems([]); setSource("empty"); }
      setGenerating(bg);
      if (bg) pollTimerRef.current = setTimeout(() => refresh(false, true), 15000);
    } else {
      setGenerating(false);
      setItems((prev) => { if (prev.length > 0) { setRefreshError(true); return prev; } return []; });
      setSource((prev) => (prev === "ai") ? "ai" : "empty");
    }
  };

  useEffect(() => {
    refresh(false);
  }, [dataAvailable]);

  return (
    <section data-testid="ceo-insights" className="space-y-3">
      <div className="flex items-end justify-between">
        <div>
          <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">Executive Insights</div>
          <div className="text-[13px] text-slate-500">
            What your CEO would tell you today
            {source === "ai" && <span className="ml-2 rounded-full bg-emerald-100 px-2 py-0.5 text-[10.5px] font-medium text-emerald-700">C-ORE generated</span>}
            {generating && <span data-testid="ceo-insights-generating-badge" className="ml-2 rounded-full bg-blue-100 px-2 py-0.5 text-[10.5px] font-medium text-blue-700">Updating…</span>}
          </div>
        </div>
        <button
          data-testid="ceo-insights-refresh"
          onClick={() => refresh(true)}
          disabled={loading}
          className="inline-flex items-center gap-1.5 rounded-md border border-slate-200 bg-white px-2.5 py-1 text-[11.5px] font-medium text-slate-600 hover:border-emerald-300 hover:text-emerald-700 disabled:opacity-50"
        >
          <RefreshCcw size={12} className={loading ? "animate-spin" : ""} />
          {loading ? "Loading…" : "Refresh insights"}
        </button>
      </div>
      {refreshError && (
        <div data-testid="ceo-insights-refresh-error" className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-[11.5px] text-amber-700">
          Refresh failed — showing previous insights. Try again shortly.
        </div>
      )}
      {generating && items.length > 0 && (
        <div data-testid="ceo-insights-updating" className="rounded-md border border-blue-200 bg-blue-50 px-3 py-2 text-[11.5px] text-blue-700">
          Updating insights in the background — new cards will appear automatically.
        </div>
      )}
      {items.length === 0 ? (
        <div data-testid="ceo-insights-empty" className="rounded-xl border border-dashed border-blue-200 bg-blue-50/50 px-4 py-8 text-center text-[12.5px] text-blue-700">
          {loading ? "Loading insights…" : generating ? "Insights are being prepared — they'll appear here shortly." : "Connect Tally and run a sync to unlock personalised strategic insights."}
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-3 tablet:grid-cols-2 desktop:grid-cols-2">
          {items.map((it) => <InsightCard key={it.id} insight={it} />)}
        </div>
      )}
    </section>
  );
}

// ---------------------------------------------------------------------------
// CEO Chat
// ---------------------------------------------------------------------------
function CEOTypingDots() {
  return (
    <div data-testid="ceo-chat-typing" className="max-w-[85%] rounded-2xl bg-[#EFF6FF] px-4 py-3">
      <div className="flex items-center gap-1">
        <span className="h-1.5 w-1.5 rounded-full bg-slate-400 core-pulse" style={{ animationDelay: "0ms" }} />
        <span className="h-1.5 w-1.5 rounded-full bg-slate-400 core-pulse" style={{ animationDelay: "200ms" }} />
        <span className="h-1.5 w-1.5 rounded-full bg-slate-400 core-pulse" style={{ animationDelay: "400ms" }} />
      </div>
    </div>
  );
}

function CEOChat() {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const sessionIdRef = useRef(`core-ceo-${Math.random().toString(36).slice(2, 10)}`);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, sending]);

  const send = async (text) => {
    const t = (text ?? input).trim();
    if (!t || sending) return;
    setMessages((m) => [...m, { role: "user", text: t }]);
    setInput("");
    setSending(true);
    const res = await api.ceoChat(t, sessionIdRef.current);
    const reply = res.ok
      ? (res.data?.reply || "Sorry, I could not process that right now. Please try again in a moment.")
      : (res.error || "Sorry, I could not process that right now. Please try again in a moment.");
    setMessages((m) => [...m, { role: "ai", text: reply }]);
    setSending(false);
  };

  return (
    <section data-testid="ceo-chat" className="rounded-xl border border-slate-200 bg-white shadow-card">
      <div className="flex items-center justify-between border-b border-slate-100 px-5 py-3">
        <div>
          <div className="text-[14px] font-bold text-[#1E3A5F]">Ask your CEO</div>
          <div className="text-[12px] text-slate-500">Strategic Q&amp;A · Hindi or English</div>
        </div>
        <Sparkles size={16} className="text-emerald-600" />
      </div>

      <div ref={scrollRef} className="space-y-2 overflow-y-auto px-5 py-3" style={{ maxHeight: 280 }}>
        {messages.length === 0 && !sending && (
          <div data-testid="ceo-chat-empty" className="rounded-lg border border-dashed border-slate-200 bg-slate-50 px-3 py-4 text-center text-[12px] text-slate-500">
            Ask any strategic question about your business. Try: &quot;Where is my biggest opportunity this quarter?&quot; or &quot;What is my one-week priority?&quot;
          </div>
        )}
        {messages.map((m, i) => (
          <div
            key={i}
            data-testid={`ceo-chat-msg-${i}`}
            className={`max-w-[88%] whitespace-pre-wrap rounded-2xl px-3 py-2 text-[12.5px] leading-snug ${
              m.role === "ai" ? "bg-[#EFF6FF] text-[#0F1B2D]" : "ml-auto bg-emerald-600 text-white"
            }`}
          >
            {m.text}
          </div>
        ))}
        {sending && <CEOTypingDots />}
      </div>

      <div className="border-t border-slate-100 px-5 py-3">
        <form onSubmit={(e) => { e.preventDefault(); send(); }} className="flex items-center gap-2">
          <input
            data-testid="ceo-chat-input"
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder={sending ? "Thinking…" : "Ask a strategic question about your business"}
            disabled={sending}
            className="flex-1 rounded-lg border border-slate-200 bg-white px-3 py-2 text-[12.5px] text-[#0F1B2D] placeholder:text-slate-400 focus:border-emerald-500 focus:outline-none disabled:bg-slate-50"
          />
          <button
            type="submit"
            data-testid="ceo-chat-send"
            disabled={sending || !input.trim()}
            className="inline-flex items-center gap-1 rounded-lg bg-emerald-600 px-3 py-2 text-[12.5px] font-semibold text-white hover:bg-emerald-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Send size={13} /> Send
          </button>
        </form>
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Active-customer module body (real data only — dash when missing)
// ---------------------------------------------------------------------------
function RealKpi({ id, label, icon: Icon, value, format = "currency", status = "blue", sub, tooltip,
                  editable = null }) {
  const s = STATUS[status] || STATUS.blue;
  return (
    <div data-testid={`ceo-kpi-${id}`}
      data-paramkey={editable?.paramKey || undefined}
      className="relative overflow-hidden rounded-lg border border-[#DBEAFE] bg-white p-4 shadow-card">
      <div className={`absolute inset-x-0 top-0 h-1 ${s.bar}`} />
      <div className="flex items-center justify-between text-[11px] font-semibold uppercase tracking-wider text-slate-500">
        <span className="inline-flex items-center gap-1.5">{Icon && <Icon size={13} className={s.text} />}{label}</span>
      </div>
      <div className="mt-2">
        {editable && editable.customerId ? (
          <span className="text-[20px] font-bold tabular-nums text-[#0F1B2D]">
            <EditableValue
              testId={`ceo-kpi-${id}-edit`}
              displayValue={editable.formatted ?? (value != null ? value : DASH)}
              rawValue={editable.rawValue}
              paramKey={editable.paramKey}
              paramLabel={label}
              customerId={editable.customerId}
              transformIn={editable.transformIn}
              transformOut={editable.transformOut}
              lastUpdatedIso={editable.lastUpdatedIso}
              onSaved={editable.onSaved}
              inputPlaceholder={editable.placeholder}
            />
          </span>
        ) : (
          <MetricDisplay value={value} format={format} label={label} tooltipText={tooltip} size="large" />
        )}
      </div>
      {sub && <div className="mt-1 text-[11.5px] text-slate-500">{sub}</div>}
    </div>
  );
}

function RealHealthGauge({ health }) {
  const score = (health?.health_score == null) ? null : Number(health.health_score);
  const breakdown = health?.component_breakdown || {};
  const labels = {
    financial_health: "Financial health (gross margin)",
    cash_position:    "Cash position (runway)",
    collection_efficiency: "Collection efficiency (DSO)",
    inventory_efficiency:  "Inventory efficiency (DIO)",
    profitability:    "Profitability (net margin)",
  };
  return (
    <section data-testid="ceo-health-gauge" className="rounded-xl border border-slate-200 bg-white p-5 shadow-card">
      <div className="flex items-end justify-between">
        <div>
          <div className="text-[14px] font-bold text-[#1E3A5F]">Business Health Score</div>
          <div className="text-[12px] text-slate-500">{score === null ? "Awaiting Tally sync" : `Risk level: ${health?.risk_level || "—"}`}</div>
        </div>
        <div className="text-right">
          {score === null
            ? <MetricDisplay value={null} format="raw" label="health" tooltipText="Auto-computed once your first Tally sync completes." size="large" />
            : <div className="text-[28px] font-bold leading-none text-[#1E3A5F]">{Math.round(score)}<span className="text-[14px] text-slate-400">/100</span></div>
          }
        </div>
      </div>
      <ul className="mt-4 space-y-3">
        {Object.entries(breakdown).map(([key, c]) => {
          const val = c?.value;
          const sc = c?.score ?? 0;
          const mx = c?.max ?? 1;
          const pct = val === null || val === undefined ? null : Math.round((sc / mx) * 100);
          const s = pct === null ? STATUS.blue
                  : pct >= 75 ? STATUS.green
                  : pct >= 50 ? STATUS.amber
                  : STATUS.red;
          return (
            <li key={key} data-testid={`ceo-health-${key}`} className="flex items-center gap-3">
              <span className="w-[100px] tablet:w-[150px] desktop:w-[180px] shrink-0 text-[12.5px] font-medium text-[#0F1B2D]">{labels[key] || key}</span>
              <div className="h-1.5 flex-1 rounded-full bg-slate-100">
                <div className={`h-1.5 rounded-full ${s.bar}`} style={{ width: `${pct ?? 0}%`, transition: "width 700ms ease" }} />
              </div>
              <span className="w-12 text-right text-[12px] font-semibold tabular-nums text-[#0F1B2D]">
                {pct === null ? DASH : `${pct}`}
              </span>
            </li>
          );
        })}
        {Object.keys(breakdown).length === 0 && (
          <li className="text-[12px] text-slate-500">Components appear after a Tally sync.</li>
        )}
      </ul>
    </section>
  );
}

function SnapshotRow({ label, value, testid }) {
  return (
    <li data-testid={testid} className="flex items-center justify-between border-b border-slate-100 py-1 last:border-b-0">
      <span className="text-slate-600">{label}</span>
      <span className={`font-semibold ${value ? "text-[#0F1B2D]" : "text-slate-400"}`}>{value ?? DASH}</span>
    </li>
  );
}

function RealCeoBody({ customerData, dataAvailable, isLoading, companyName, reload }) {
  const raw   = customerData?.raw           || {};
  const prof  = customerData?.profitability || {};
  const liq   = customerData?.liquidity     || {};
  const eff   = customerData?.efficiency    || {};
  const ceo   = customerData?.ceo           || {};
  const health = customerData?.health       || {};
  const params = customerData?.params       || {};

  const revVsTarget = ceo.revenue_vs_target_pct;
  const revPerEmp   = ceo.revenue_per_employee;
  const clientConc  = ceo.client_concentration_pct;
  const nps         = params.nps_score ?? null;

  // Customer id for inline edits — falls back to session.
  const customerId = customerData?.customer_id || (() => { try { return getCurrentCustomer()?.id || null; } catch (_e) { return null; } })();
  const updatedAt = params.params_updated_at || {};
  const handleSaved = () => { if (typeof reload === "function") reload(); };

  return (
    <div className="space-y-5">
      {/* KPI cards */}
      <section data-testid="ceo-kpis" className="grid grid-cols-2 gap-3 desktop:grid-cols-6">
        <RealKpi id="health"     label="Business Health Score" icon={Activity}
          value={health.health_score ?? null} format="number" status="amber"
          sub={health.risk_level ? `Risk: ${health.risk_level}` : null}
          tooltip="Auto-computed once your first Tally sync completes." />
        <RealKpi id="rev_target" label="Revenue vs Target" icon={Target}
          value={revVsTarget} format="percentage"
          status={revVsTarget !== null && revVsTarget >= 90 ? "green" : revVsTarget !== null && revVsTarget >= 70 ? "amber" : "blue"}
          sub={params.revenue_target ? `target ₹${(params.revenue_target / 1_00_00_000).toFixed(2)} Cr / yr` : "Set a revenue target →"}
          tooltip="Annual revenue target. Edit inline to update across modules."
          editable={{
            customerId,
            paramKey: "revenue_target",
            rawValue: params.revenue_target,
            // Stored in raw rupees; edit in Cr for readability.
            transformIn:  (v) => v ? (Number(v) / 1_00_00_000).toString() : "",
            transformOut: (v) => v === "" ? null : Math.round(Number(v) * 1_00_00_000),
            lastUpdatedIso: updatedAt.revenue_target,
            onSaved: handleSaved,
            placeholder: "6.0 (in ₹ Cr)",
            formatted: (revVsTarget !== null && revVsTarget !== undefined) ? `${Number(revVsTarget).toFixed(1)}%` : (params.revenue_target ? `₹${(params.revenue_target / 1_00_00_000).toFixed(2)}Cr` : DASH),
          }} />
        <RealKpi id="capacity"   label="Capacity Utilisation" icon={Building2}
          value={null} format="percentage" status="blue"
          tooltip="Connects with your production system (MES/ERP)." />
        <RealKpi id="concent"    label="Client Concentration" icon={Users}
          value={clientConc} format="percentage"
          status={clientConc !== null && clientConc >= 60 ? "red" : clientConc !== null && clientConc >= 40 ? "amber" : "green"}
          sub={clientConc !== null ? "Top 2 customers share" : null}
          tooltip="Top-2 customer share of receivables. Computed from your Tally sundry debtors." />
        <RealKpi id="rev_emp"    label="Revenue per Employee" icon={TrendingUp}
          value={revPerEmp} format="currency" status="green"
          sub={params.employee_count ? `${params.employee_count} employees` : "Set employee count →"}
          tooltip="Annualised revenue ÷ headcount. Edit headcount inline."
          editable={{
            customerId,
            paramKey: "employee_count",
            rawValue: params.employee_count,
            transformIn:  (v) => v ?? "",
            transformOut: (v) => v === "" ? null : Math.round(Number(v)),
            lastUpdatedIso: updatedAt.employee_count,
            onSaved: handleSaved,
            placeholder: "14",
            formatted: revPerEmp !== null ? fmtINR(revPerEmp) : (params.employee_count ? `${params.employee_count} emp` : DASH),
          }} />
        <RealKpi id="nps"        label="NPS Score" icon={Award}
          value={nps} format="number"
          status={nps !== null && nps >= 50 ? "green" : nps !== null && nps >= 30 ? "amber" : "blue"}
          sub={nps !== null ? "from your last survey" : "Tap pencil to set"}
          tooltip="Net Promoter Score from your last customer survey."
          editable={{
            customerId,
            paramKey: "nps_score",
            rawValue: nps,
            transformIn:  (v) => v ?? "",
            transformOut: (v) => v === "" ? null : Math.round(Number(v)),
            lastUpdatedIso: updatedAt.nps_score,
            onSaved: handleSaved,
            placeholder: "62",
            formatted: nps !== null ? String(nps) : DASH,
          }} />
      </section>

      {/* AI Strategic Insights */}
      <InsightsGrid dataAvailable={dataAvailable} />

      {/* Strategic snapshot — real-only */}
      <section data-testid="ceo-snapshot" className="grid grid-cols-1 gap-5 tablet:grid-cols-2 desktop:grid-cols-2">
        <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-card">
          <div className="text-[14px] font-bold text-[#1E3A5F]">Strategic snapshot{companyName ? ` · ${companyName}` : ""}</div>
          <div className="text-[12px] text-slate-500">Real numbers from your latest Tally sync</div>
          <ul className="mt-4 space-y-2 text-[13px]">
            <SnapshotRow label="Revenue MTD"      value={fmtINR(raw.revenue_mtd)} testid="snap-revenue" />
            <SnapshotRow label="Net Profit"       value={fmtINR(raw.net_profit)}  testid="snap-net-profit" />
            <SnapshotRow label="Gross Margin"     value={prof.gross_margin_pct != null ? `${Number(prof.gross_margin_pct).toFixed(1)}%` : null} testid="snap-gm" />
            <SnapshotRow label="Cash Runway"      value={liq.cash_runway_days != null ? `${liq.cash_runway_days} days` : null} testid="snap-runway" />
            <SnapshotRow label="Working Capital"  value={fmtINR(liq.working_capital)} testid="snap-wc" />
            <SnapshotRow label="DSO"              value={eff.dso_days != null ? `${eff.dso_days} days` : null} testid="snap-dso" />
            <SnapshotRow label="CCC"              value={eff.ccc_days != null ? `${eff.ccc_days} days` : null} testid="snap-ccc" />
          </ul>
        </div>
        <RealHealthGauge health={health} />
      </section>

      {/* OKR Tracker — live editor backed by params.okrs */}
      <section data-testid="ceo-okrs" className="rounded-xl border border-slate-200 bg-white p-5 shadow-card">
        <div className="flex items-end justify-between">
          <div>
            <div className="text-[14px] font-bold text-[#1E3A5F]">OKR Tracker</div>
            <div className="text-[12px] text-slate-500">Quarterly objectives, key results & progress — edit any cell, hit Save</div>
          </div>
        </div>
        <div className="mt-4">
          <OKREditor
            customerId={customerId}
            value={params.okrs}
            lastUpdatedIso={updatedAt.okrs}
            onSaved={handleSaved}
            disabled={!customerId}
          />
        </div>
      </section>

      {/* Department budgets editor */}
      <section data-testid="ceo-dept-budgets" className="rounded-xl border border-slate-200 bg-white p-5 shadow-card">
        <div className="text-[14px] font-bold text-[#1E3A5F]">Department Budgets</div>
        <div className="text-[12px] text-slate-500">Allocate monthly spend across departments — drives margin alerts in the CFO module</div>
        <div className="mt-4">
          <DeptBudgetsEditor
            customerId={customerId}
            value={params.dept_budgets}
            lastUpdatedIso={updatedAt.dept_budgets}
            onSaved={handleSaved}
            disabled={!customerId}
          />
        </div>
      </section>

      {/* Risk register placeholder */}
      <section data-testid="ceo-risks" className="rounded-xl border border-slate-200 bg-white p-5 shadow-card">
        <div className="text-[14px] font-bold text-[#1E3A5F]">Risk Register</div>
        <div className="text-[12px] text-slate-500">Top risks identified for your business</div>
        <div className="mt-4 flex items-start gap-2 rounded-md border border-dashed border-slate-200 bg-slate-50 px-3 py-6 text-[12.5px] text-slate-500">
          <span className="text-slate-400 text-[18px] leading-none">{DASH}</span>
          <span>Risks appear here once captured. Smart alerts from your live data already power the Executive Dashboard.</span>
        </div>
      </section>

      {/* CEO chat (already wired to real context) */}
      <CEOChat />
      {void isLoading || null}
    </div>
  );
}

export default function VirtualCEO() {
  const { customerData, dataAvailable, isLoading, companyName, reload } = useCustomerData();
  return (
    <div data-testid="ceo-module">
      <ConnectTallyBanner dataAvailable={dataAvailable} isLoading={isLoading} />
      <div className="space-y-5 px-4 tablet:px-5 desktop:px-6 py-4 desktop:py-5">
      <div className="flex items-end justify-between">
        <div>
          <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">CEO</div>
          <h2 className="text-[20px] font-bold text-[#1E3A5F]">{companyName ? `${companyName} — your strategic co-pilot` : "Your strategic co-pilot · Q4 FY26"}</h2>
        </div>
        <div className="hidden md:flex items-center gap-2 text-[12px] text-slate-500">
          <CheckCircle2 size={13} className="text-emerald-600" />
          Last reviewed today
        </div>
      </div>

      <RealCeoBody
        customerData={customerData}
        dataAvailable={dataAvailable}
        isLoading={isLoading}
        companyName={companyName}
        reload={reload}
      />
      </div>
    </div>
  );
}
